import { Component, ElementRef, EventEmitter, Input, OnChanges, OnInit, Output, ViewChild } from "@angular/core";
import { Configuration } from "../core/configuration";
import { ConfigurationLoader } from "../core/services/configuration-loader.service";
import { DeviceDetectorService } from 'ngx-device-detector';

@Component({
    selector: "serprobots-message-input",
    templateUrl: "message-input.component.html",
    styleUrls: ["message-input.component.css"]
})
export class MessageInputComponent implements OnInit, OnChanges {

    @ViewChild('input')
    private inputElementRef!: ElementRef;

    // Evento que é emitido toda vez que o usuário deseja enviar uma mensagem para o chatbot.
    @Output()
    onNewUserMessage: EventEmitter<string> = new EventEmitter<string>();

    // Evento que é emitido toda vez que o usuário deseja reiniciar a conversação.
    @Output()
    onRestart: EventEmitter<boolean> = new EventEmitter<boolean>();

    // Evento que é emitido quando o usuário deseja realizar uma avaliação do atendimento.
    @Output()
    onShowAssesment: EventEmitter<boolean> = new EventEmitter<boolean>();

    @Input()
    disabled: boolean = false;

    // Define se o botão de avaliação deve ser exibido ou não.
    @Input()
    showAssesmentButton: boolean = true;

    message: string = "";
    configuration: Configuration = new Configuration({});

    constructor(
        private configurationLoader: ConfigurationLoader,
        private deviceService: DeviceDetectorService
    ) { }

    /**
     * Verificar as mudanças no estado do componente.
     * 
     * Uma mudança que deve-se estar atento é a mudança do Input entre habilitado e desabilitado.
     * Quando o Input se torna habilitado, deve-se dar o foco a ele novamente.
     */
    async ngOnChanges(): Promise<void> {
        if (this.inputElementRef && !this.disabled) {
            setTimeout(() => {
                if (this.deviceService.isDesktop()) {
                    this.inputElementRef.nativeElement.focus();
                }
            });
        }
    }

    /**
     * Carregar as configurações, necessárias para definir comportamentos e visual da tela.
     * 
     * Observação: o await não bloqueia a renderização da tela! Ele é ignorado pelo Angular,
     * portanto, a configuração pode estar nula. Por este motivo, a tela é iniciada com uma configuração vazia.
     */
    async ngOnInit(): Promise<void> {
        this.configuration = await this.configurationLoader.getConfiguration();
    }

    /**
     * Disponibiliza para o template o texto que deve ser usado como PlaceHolder do Input.
     */
    get inputPlaceHolder(): string {
        return this.configuration.inputPlaceHolder!;
    }

    /**
     * Disponibiliza para o Template qual a classe de tipografia que deve ser usada.
     * 
     * @returns Classe de Tipografia.
     */
    getTypographyClass(): string {
        return `mat-typography ${this.configuration.typography || ""}`;
    }

    /**
     * Usado pelo template para pedir o reinicio da conversação.
     * Repassa para que o Emitter lance o evento.
     */
    restart(): void {
        this.onRestart.emit(true);
        this.message = "";
    }

    /**
     * Evento que trata quando o usuário aperta "Enter" dentro da caixa de texto.
     * Precisa verificar as configurações para confirmar se deve enviar ou não a mensagem neste evento.
     */
    onEnter(): void {
        if (this.configuration.useEnterToSendMessage) {
            this.send();
        }
    }

    /**
     * Usado pelo template para pedir a exibição da tela de avaliação.
     * Repassa para o EventEmitter propagar o evento para fora do componetne.
     */
    showAssesment(): void {
        this.onShowAssesment.emit(true);
    }

    /**
     * Chamado quando o usuário digitou uma mensagem.
     * Repassa para o Emitter do componente e depois apaga o campo, conforme definido nas configurações.
     */
    send() {
        if (this.message && this.message.trim() != "") {
            this.onNewUserMessage.emit(this.message.trim());

            if (this.configuration.clearInput) {
                this.message = "";
            }
        } else {
            this.message = "";
        }
    }
}